import { createFileRoute, useRouter } from "@tanstack/react-router";
import { LayoutHeader } from "@/Header";
import { Button } from "@/Button";
import { useState, useEffect, useCallback } from "react";
import defaultPet from "$/defaultLogo.svg";
import {
  useBusinessReserveDetail,
  useUpdateBusinessReserve,
} from "../../../api/services/business/reserve";
import { Dialog } from "@/Dialog";

interface ReservationParams {
  reservationSeq: string;
  source?: string; // 출처 구분: "approval" | "confirm"
}

export const Route = createFileRoute("/business/layout/revservation")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): ReservationParams => {
    return {
      reservationSeq: search.reservationSeq as string,
      source: search.source as string | undefined,
    };
  },
});

function RouteComponent() {
  const { history } = useRouter();
  const { reservationSeq, source } = Route.useSearch();

  const {
    data: reservationData,
    isLoading,
    error,
  } = useBusinessReserveDetail(parseInt(reservationSeq));

  // 모달 상태 관리
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: "approve" | "cancel";
    reservationSeq: number;
    petName: string;
  } | null>(null);
  const [lastActionType, setLastActionType] = useState<
    "approve" | "cancel" | null
  >(null);

  // 예약 상태 업데이트 훅
  const updateReserveMutation = useUpdateBusinessReserve();

  // API 데이터에서 필요한 정보 추출
  const petInfo = reservationData?.data?.props?.find(
    (prop) => prop.type === "petInfo"
  )?.datas?.[0] as any;
  // const additionalServices = reservationData?.data?.props?.find(
  //   (prop) => prop.type === "additionals"
  // )?.datas as any[];
  const personalityData = reservationData?.data?.props?.find(
    (prop) => prop.type === "personality"
  )?.datas as any[];
  const ticketInfo = reservationData?.data?.props?.find(
    (prop) => prop.type === "ticketInfo"
  )?.datas as any[];

  // 유치원 / 호텔 여부 — 이용권의 cdProductType 우선, 없으면 reservation 라벨로 fallback
  const isKindergarten =
    ticketInfo?.[0]?.cdProductType === "PRODUCT_TYPE_KINDERGARTEN" ||
    reservationData?.data?.reservation === "수업";
  const isHotel =
    ticketInfo?.[0]?.cdProductType === "PRODUCT_TYPE_HOTEL" ||
    reservationData?.data?.reservation === "호텔";

  // 날짜 분할 — date 응답 형식: "yyyy-MM-dd ~ yyyy-MM-dd"
  const dateParts = (reservationData?.data?.date ?? "")
    .split("~")
    .map((s) => s.trim())
    .filter(Boolean);
  const dateStart = dateParts[0] ?? "";
  const dateEnd = dateParts[1] ?? "";
  const hasDateRange = !!dateEnd && dateEnd !== dateStart;

  // 유치원은 시작일만 표시
  const displayDate = isKindergarten
    ? dateStart
    : (reservationData?.data?.date ?? "");

  // 버튼 노출은 예약의 실제 상태 기준 — 승인은 대기중일 때만, 취소는 대기중/확정일 때만.
  // 상태값이 없는 구버전 응답에서는 기존 source 파라미터 분기로 fallback
  const reservStatus = reservationData?.data?.cdReservStatus;
  const isPendingApproval =
    reservStatus === "RESERV_STATUS_REQUESTED" ||
    reservStatus === "RESERV_STATUS_UPDATED";
  const showApproveButton = reservStatus
    ? isPendingApproval
    : source !== "confirm";
  const showCancelButton = reservStatus
    ? isPendingApproval || reservStatus === "RESERV_STATUS_CONFIRMED"
    : true;

  // 예약 승인 확인 핸들러
  const handleApprove = useCallback(() => {
    if (!reservationData?.data) return;

    setPendingAction({
      type: "approve",
      reservationSeq: reservationData.data.reservationSeq,
      petName: petInfo?.petName || "반려동물",
    });
    setShowConfirmModal(true);
  }, [reservationData, petInfo]);

  // 예약 취소 확인 핸들러
  const handleCancel = useCallback(() => {
    if (!reservationData?.data) return;

    setPendingAction({
      type: "cancel",
      reservationSeq: reservationData.data.reservationSeq,
      petName: petInfo?.petName || "반려동물",
    });
    setShowConfirmModal(true);
  }, [reservationData, petInfo]);

  // 확인 모달에서 API 호출
  const handleConfirmAction = useCallback(() => {
    if (!pendingAction) return;

    const { type, reservationSeq } = pendingAction;
    const cdReservStatus =
      type === "approve" ? "RESERV_STATUS_CONFIRMED" : "RESERV_STATUS_CANCELED";

    // 액션 타입 저장
    setLastActionType(type);

    updateReserveMutation.mutate({
      reservationSeq,
      cdReservStatus,
    });

    setShowConfirmModal(false);
    setPendingAction(null);
  }, [pendingAction, updateReserveMutation]);

  // API 성공 시 성공 모달 표시
  useEffect(() => {
    if (updateReserveMutation.isSuccess) {
      setShowSuccessModal(true);
    }
  }, [updateReserveMutation.isSuccess]);

  // 성공 모달 닫기 및 이전 페이지 이동
  const handleSuccessModalClose = useCallback(() => {
    setShowSuccessModal(false);
    history.back();
  }, [history]);

  // 초기값 설정

  if (isLoading) {
    return (
      <div className="flex flex-col flex-auto min-h-0 bg-gray-50">
        <LayoutHeader title="예약정보 상세" hasBackBtn hasFootPrint={false} />
        <div className="flex flex-1 justify-center items-center">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </div>
    );
  }

  if (error || !reservationData?.data) {
    return (
      <div className="flex flex-col flex-auto min-h-0 bg-gray-50">
        <LayoutHeader title="예약정보 상세" hasBackBtn hasFootPrint={false} />
        <div className="flex flex-1 justify-center items-center">
          <div className="text-red-500">예약 정보를 불러올 수 없습니다.</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <LayoutHeader title="예약정보 상세" hasBackBtn hasFootPrint={false} />
      {/* 다른 화면들과 동일한 표준 구조 — 콘텐츠가 자라고 main 이 스크롤, nav 여백은 레이아웃(pb-24)이 처리 */}
      <div className="flex-auto bg-[#F5F5F5]">
        <div className="p-4 space-y-4">
        {/* 반려동물 정보 카드 */}
        <div className="p-4 bg-white rounded-xl border-2 border-orange-400">
          <div className="flex gap-4 items-center">
            <img
              src={petInfo?.thumbnailUrl || defaultPet}
              alt={petInfo?.petName || "반려동물"}
              className="object-cover w-16 h-16 rounded-full"
            />
            <div className="flex-1">
              <div className="text-lg font-bold text-gray-800">
                {petInfo?.petName || "이름 없음"}
              </div>
              <div className="text-sm text-gray-600">
                {petInfo?.petWeight || ""} • {petInfo?.petBreed || ""}
              </div>
            </div>
          </div>
        </div>

        {/* 예약 정보 (예약 유형 + 일시) */}
        <div className="p-4 space-y-3 bg-white rounded-xl">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">예약 유형</span>
            <span className="font-medium">
              {reservationData.data.reservation}
            </span>
          </div>

          <div
            className={`flex justify-between ${isHotel && hasDateRange ? "items-start" : "items-center"}`}
          >
            <span className="text-gray-600">
              {isKindergarten ? "시작 일시" : "예약 일시"}
            </span>
            {isHotel && hasDateRange ? (
              <div className="flex flex-col items-end font-medium leading-tight">
                <span>체크인 {dateStart}</span>
                <span className="mt-0.5">체크아웃 {dateEnd}</span>
              </div>
            ) : (
              <span className="font-medium">{displayDate}</span>
            )}
          </div>
        </div>

        {/* 사용한 이용권 (별도 섹션) */}
        {ticketInfo && ticketInfo.length > 0 && (
          <div className="p-4 bg-white rounded-xl">
            <h3 className="mb-3 font-bold text-gray-800">사용한 이용권</h3>
            <div className="flex flex-col gap-2">
              {ticketInfo.map((ticket: any, index: number) => (
                <div
                  key={index}
                  className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium text-gray-800">
                        {ticket.name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {ticket.contents}
                      </div>
                    </div>
                    {ticket.badge && ticket.badge.length > 0 && (
                      <div className="flex gap-1">
                        {ticket.badge.map(
                          (badge: string, badgeIndex: number) => (
                            <span
                              key={badgeIndex}
                              className="px-2 py-1 text-xs font-medium text-orange-500 bg-orange-50 rounded"
                            >
                              {badge}
                            </span>
                          )
                        )}
                      </div>
                    )}
                  </div>
                  {ticket.cdProductType !== "PRODUCT_TYPE_HOTEL" &&
                    typeof ticket.totalQty === "number" &&
                    typeof ticket.remainQty === "number" && (
                      <div className="mb-2 text-xs">
                        <span className="text-gray-700">
                          {ticket.totalQty - ticket.remainQty}회 사용
                        </span>
                        <span
                          className={
                            ticket.remainQty === 0
                              ? "ml-1 text-gray-400"
                              : "ml-1 font-medium text-orange-500"
                          }
                        >
                          ({ticket.remainQty}회 남음)
                        </span>
                      </div>
                    )}
                  <div className="flex justify-between items-center text-sm">
                    {ticket.expiredDate && (
                      <span className="text-xs text-gray-500">
                        만료일: {ticket.expiredDate}
                      </span>
                    )}
                    <span className="font-medium text-gray-800">
                      {ticket.amount?.toLocaleString()}원
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 반려견 정보 */}
        <div className="p-4 bg-white rounded-xl">
          <h3 className="mb-3 font-bold text-gray-800">반려견 정보</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-gray-600">{petInfo?.petBreed || ""}</div>
            <div className="text-gray-600">{petInfo?.petAge || ""}</div>
            <div className="text-gray-600">{petInfo?.petWeight || ""}</div>
            <div className="text-gray-600">
              {petInfo?.neuter ? "중성화 완료" : "중성화 미완료"}
            </div>
          </div>

          {/* 성격/습관 */}
          <div className="mt-4">
            <div className="grid grid-cols-3 gap-2">
              {personalityData?.map((trait, index) => (
                <button
                  key={index}
                  className={`px-3 py-2 text-xs rounded-lg border ${
                    trait.active
                      ? "border-orange-400 text-orange-400 bg-orange-50"
                      : "border-gray-200 text-gray-500 bg-gray-50"
                  }`}
                >
                  {trait.personalityName}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 알레르기 정보 */}
        <div className="p-4 bg-white rounded-xl">
          <h3 className="mb-3 font-bold text-gray-800">알레르기 정보</h3>
          <div className="p-3 w-full min-h-[80px] rounded-lg border border-gray-200 bg-gray-50">
            {reservationData?.data?.allergy || "알레르기 정보가 없습니다."}
          </div>
        </div>

        {/* 보호자 메시지 */}
        <div className="p-4 bg-white rounded-xl">
          <h3 className="mb-3 font-bold text-gray-800">
            보호자 메시지(참고사항)
          </h3>
          <div className="p-3 w-full min-h-[80px] rounded-lg border border-gray-200 bg-gray-50">
            {reservationData?.data?.memo || "보호자 메시지가 없습니다."}
          </div>
        </div>

        {/* 하단 버튼 - 예약 취소(대기중/확정), 예약 승인(승인 대기 시에만) - 캘린더와 동일 useUpdateBusinessReserve + RESERV_STATUS_CANCELED */}
        {(showCancelButton || showApproveButton) && (
          <div className="w-full px-5 py-3 bg-[#F5F5F5] flex gap-2">
            {showCancelButton && (
              <Button
                className="w-full bg-[#F5F5F5]"
                style={{ pointerEvents: "auto" }}
                onClick={handleCancel}
              >
                <div className="py-3 mx-auto w-full font-semibold text-center text-yellow-500 bg-white rounded-full border border-yellow text-[16px] text-yellow">
                  예약 취소
                </div>
              </Button>
            )}
            {showApproveButton && (
              <Button
                className="w-full bg-[#F5F5F5]"
                style={{ pointerEvents: "auto" }}
                onClick={handleApprove}
              >
                <div className="py-3 mx-auto w-full font-semibold text-center text-white bg-orange-400 rounded-full text-[16px]">
                  예약 승인
                </div>
              </Button>
            )}
          </div>
        )}
        </div>
      </div>

      {/* 확인 모달 */}
      <Dialog
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
      >
        <div className="p-6 text-center">
          <h3 className="mb-4 text-lg font-bold">
            {pendingAction?.type === "approve" ? "예약 승인" : "예약 취소"}
          </h3>
          <p className="mb-6 text-sm text-gray-600">
            {pendingAction?.petName}의 예약을{" "}
            {pendingAction?.type === "approve" ? "승인" : "취소"}하시겠습니까?
          </p>
          <div className="flex gap-3">
            <button
              className="flex-1 py-2 font-semibold text-gray-700 bg-white rounded-lg border border-gray-300"
              onClick={() => setShowConfirmModal(false)}
            >
              취소
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-lg"
              onClick={handleConfirmAction}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 성공 모달 */}
      <Dialog visible={showSuccessModal} onClose={handleSuccessModalClose}>
        <div className="p-6 text-center">
          <h3 className="mb-4 text-lg font-bold">처리 완료</h3>
          <p className="mb-6 text-sm text-gray-600">
            예약이 성공적으로 {lastActionType === "approve" ? "승인" : "취소"}
            되었습니다.
          </p>
          <div className="flex justify-center">
            <button
              className="px-8 py-3 font-semibold text-white bg-orange-400 rounded-lg"
              onClick={handleSuccessModalClose}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
